import { useFormikContext } from "formik";
import { React, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  listarDepartamentos,
  listarLocalidades,
  listarProvincias,
  listarZonificaciones,
} from "../api/General";
import { useCatalog } from "../context/Catalog";
import { useTramite } from "../context/Tramite";
import { isInvalid } from "../utils/forms";

import "./css/components.css";
import { isEditable } from "../utils/tramite";
import "leaflet/dist/leaflet.css";
import "leaflet/dist/leaflet";
import Loader from "./controls/Loader";
import Grid from "./layout/Grid";
import Row from "./layout/Row";
import Col from "./layout/Col";
import Label from "./controls/Label";
import ObjectField from "./controls/ObjectField";
import TextField from "./controls/TextField";
import OptionalTextField from "./controls/OptionalTextField";
import SelectField from "./controls/SelectField";
import PointField from "./controls/PointField";
import MapField from "./controls/MapField";

export default function EstablecimientoForm({ ...props }) {
  const { tramite } = useTramite();
  const tramiteForm = useFormikContext();
  const { t } = useTranslation();
  const provinciaCodigo = tramiteForm.values.domicilio?.provincia?.codigo;
  const departamentoCodigo = tramiteForm.values.domicilio?.departamento?.codigo;
  const localidadCodigo = tramiteForm.values.domicilio?.localidad?.codigo;
  const provinciasCatalog = useCatalog(
    ["provincias", "establecimiento", "02", "06"],
    () => listarProvincias({ codigo: ["02", "06"] }),
  );
  const departamentosCatalog = useCatalog(
    provinciaCodigo
      ? ["departamentos", "establecimiento", provinciaCodigo]
      : null,
    () => listarDepartamentos({ provincia: provinciaCodigo }),
  );
  const localidadesCatalog = useCatalog(
    provinciaCodigo && departamentoCodigo
      ? ["localidades", "establecimiento", provinciaCodigo, departamentoCodigo]
      : null,
    () =>
      listarLocalidades({
        provincia: provinciaCodigo,
        departamento: departamentoCodigo,
      }),
  );
  const zonificacionesCatalog = useCatalog(["zonificaciones"], () =>
    listarZonificaciones({}),
  );
  const provincias = provinciasCatalog.data || [];
  const departamentos = provinciaCodigo ? departamentosCatalog.data || [] : [];
  const localidades =
    provinciaCodigo && departamentoCodigo ? localidadesCatalog.data || [] : [];
  const zonificaciones = zonificacionesCatalog.data || [];
  const loading =
    provinciasCatalog.loading ||
    departamentosCatalog.loading ||
    localidadesCatalog.loading ||
    zonificacionesCatalog.loading;
  const handleDomicilioChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleUbicacionChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleZonaEmplazamientoChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleHabilitacionMunicipal = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleAccesoSecundarioChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleTelefonoChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleCorreoElectronicoChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleWebChange = (event) => {
    tramiteForm.handleChange(event);
  };
  useEffect(() => {
    if (
      provinciaCodigo &&
      (!departamentoCodigo ||
        !departamentoCodigo.startsWith(provinciaCodigo))
    ) {
      tramiteForm.setFieldValue("domicilio.departamento", null, false);
      tramiteForm.setFieldValue("domicilio.localidad", null, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinciaCodigo]);
  useEffect(() => {
    if (!departamentoCodigo) {
      tramiteForm.setFieldValue("domicilio.localidad", null, false);
    } else if (
      localidadCodigo &&
      !localidadCodigo.startsWith(departamentoCodigo)
    ) {
      tramiteForm.setFieldValue("domicilio.localidad", null, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departamentoCodigo]);
  return (
    <Loader loading={loading && !tramite}>
      <Grid>
        <Row>
          <Col>
            <Label for="domicilio">{t("tramite.domicilio")}</Label>
            <ObjectField
              id="domicilio"
              name="domicilio"
              value={tramiteForm.values?.domicilio}
              onChange={handleDomicilioChange}
            >
              {({ value: domicilio, setProperty: setDomicilioProperty }) => (
                <Grid>
                  <Row>
                    <Col lg={7}>
                      <TextField
                        id="domicilio_calle"
                        name="domicilio.calle"
                        label={t("direccion.calle")}
                        value={domicilio?.calle}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty("calle", event.target.value);
                        }}
                        isInvalid={isInvalid("domicilio.calle", tramiteForm)}
                        error={t(tramiteForm.errors.domicilio?.calle)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                    <Col lg={2}>
                      <TextField
                        id="domicilio_altura"
                        name="domicilio.altura"
                        label={t("direccion.altura")}
                        value={domicilio?.altura}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty("altura", event.target.value);
                        }}
                        isInvalid={isInvalid("domicilio.altura", tramiteForm)}
                        error={t(tramiteForm.errors.domicilio?.altura)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                    <Col lg={3}>
                      <OptionalTextField
                        id="domicilio_unidadFuncional"
                        name="domicilio.unidadFuncional"
                        label={t("direccion.unidadFuncional")}
                        value={domicilio?.unidadFuncional}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty(
                            "unidadFuncional",
                            event.target.value,
                          );
                        }}
                        isInvalid={isInvalid(
                          "domicilio.unidadFuncional",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.domicilio?.unidadFuncional)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={6}>
                      <SelectField
                        id="domicilio_provincia"
                        name="domicilio.provincia"
                        label={t("direccion.provincia")}
                        value={domicilio?.provincia}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty("provincia", event.target.value);
                        }}
                        options={provincias}
                        valueAttr="codigo"
                        labelAttr="nombre"
                        emptyLabel="..."
                        isInvalid={isInvalid(
                          "domicilio.provincia",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.domicilio?.provincia?.codigo,
                        )}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                    <Col lg={6}>
                      <SelectField
                        id="domicilio_departamento"
                        name="domicilio.departamento"
                        label={t("direccion.departamento")}
                        value={domicilio?.departamento}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty(
                            "departamento",
                            event.target.value,
                          );
                        }}
                        options={departamentos}
                        valueAttr="codigo"
                        labelAttr="nombre"
                        emptyLabel="..."
                        isInvalid={isInvalid(
                          "domicilio.departamento",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.domicilio?.departamento)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={10}>
                      <SelectField
                        id="domicilio_localidad"
                        name="domicilio.localidad"
                        label={t("direccion.localidad")}
                        value={domicilio?.localidad}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty("localidad", event.target.value);
                        }}
                        options={localidades}
                        valueAttr="codigo"
                        labelAttr="nombre"
                        emptyLabel="..."
                        isInvalid={isInvalid(
                          "domicilio.localidad",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.domicilio?.localidad?.codigo,
                        )}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                    <Col lg={2}>
                      <TextField
                        id="domicilio_codigoPostal"
                        name="domicilio.codigoPostal"
                        label={t("direccion.codigoPostal")}
                        value={domicilio?.codigoPostal}
                        onChange={(event) => {
                          event.preventDefault();
                          setDomicilioProperty(
                            "codigoPostal",
                            event.target.value,
                          );
                        }}
                        isInvalid={isInvalid(
                          "domicilio.codigoPostal",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.domicilio?.codigoPostal)}
                        disabled={!isEditable(tramite)}
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
            </ObjectField>
          </Col>
        </Row>
        <Row>
          <Col>
            <PointField
              id="ubicacion"
              name="ubicacion"
              label={t("tramite.ubicacion")}
              value={tramiteForm.values.ubicacion}
              onChange={handleUbicacionChange}
              isInvalid={isInvalid("ubicacion", tramiteForm)}
              error={t(tramiteForm.errors.ubicacion)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <MapField
              name="ubicacion"
              value={tramiteForm.values.ubicacion}
              zoom={18}
              zoomControl={isEditable(tramite)}
              dragging={isEditable(tramite)}
              onChange={handleUbicacionChange}
              scrollWheelZoom={isEditable(tramite)}
              url={process.env.REACT_APP_MAP_URL}
              attribution={process.env.REACT_APP_MAP_ATTRIBUTION}
              mapStyle={{ height: "400px", marginBottom: "30px" }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <SelectField
              id="zonaEmplazamiento"
              name="zonaEmplazamiento"
              label={t("tramite.zonaEmplazamiento")}
              value={tramiteForm.values.zonaEmplazamiento}
              onChange={handleZonaEmplazamientoChange}
              options={zonificaciones}
              valueAttr="codigo"
              labelAttr="nombre"
              emptyLabel="..."
              isInvalid={isInvalid("zonaEmplazamiento", tramiteForm)}
              error={t(tramiteForm.errors.zonaEmplazamiento)}
              disabled={!isEditable(tramite)}
            />
          </Col>
          <Col md={6}>
            <TextField
              id="habilitacionMunicipal"
              name="habilitacionMunicipal"
              label={t("tramite.habilitacionMunicipal")}
              value={tramiteForm.values.habilitacionMunicipal}
              onChange={handleHabilitacionMunicipal}
              isInvalid={isInvalid("habilitacionMunicipal", tramiteForm)}
              error={t(tramiteForm.errors.habilitacionMunicipal)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <OptionalTextField
              id="accesoSecundario"
              name="accesoSecundario"
              label={t("tramite.accesoSecundario")}
              value={tramiteForm.values.accesoSecundario}
              onChange={handleAccesoSecundarioChange}
              isInvalid={isInvalid("accesoSecundario", tramiteForm)}
              error={t(tramiteForm.errors.accesoSecundario)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={5} md={3}>
            <TextField
              id="telefono"
              name="telefono"
              label={t("tramite.telefono")}
              value={tramiteForm.values.telefono}
              onChange={handleTelefonoChange}
              isInvalid={isInvalid("telefono", tramiteForm)}
              error={t(tramiteForm.errors.telefono)}
              disabled={!isEditable(tramite)}
            />
          </Col>
          <Col lg={7} md={9}>
            <TextField
              id="correoElectronico"
              name="correoElectronico"
              label={t("tramite.correoElectronico")}
              value={tramiteForm.values.correoElectronico}
              onChange={handleCorreoElectronicoChange}
              isInvalid={isInvalid("correoElectronico", tramiteForm)}
              error={t(tramiteForm.errors.correoElectronico)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <OptionalTextField
              id="web"
              name="web"
              label={t("tramite.web")}
              value={tramiteForm.values.web}
              onChange={handleWebChange}
              isInvalid={isInvalid("web", tramiteForm)}
              error={t(tramiteForm.errors.web)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
      </Grid>
    </Loader>
  );
}
